import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useInterval } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { ScoreStatus } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconMinus } from '#src/atoms/icons/system/iconMinus.ts';
import { iconPlusCircle } from '#src/atoms/icons/system/iconPlusCircle.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyL, BrandL, BrandM } from '#src/atoms/typography/index.tsx';
import { CardImg } from '#src/components/card/CardImg.tsx';
import { Score } from '#src/components/scoring/Score/index.tsx';
import { ScoreTimeline } from '#src/components/scoring/ScoreTimeline/index.tsx';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import type { ScorePoints_card } from './__generated__/index.graphql.ts';
import bg from './assets/bg.webp';

const scaleIn = keyframes`
 50% {
    scale: 1.5;
  }
  100% {
    opacity: 1;
    scale: 1;
  }
`;

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: calc(var(--unit) * 5);
  align-items: center;
  padding: calc(var(--unit) * 8) 0;
  background: no-repeat center / cover url(${bg});
`;

const TextContainer = styled(Vertical).attrs({ gap: 4 })`
  align-items: center;
  width: 100%;
  text-align: center;
  @media ${laptopAndAbove} {
    width: 50%;
  }
`;

const CardContainer = styled(Vertical).attrs({ gap: 1, center: true })`
  width: 168px;
`;

const TimelineWrapper = styled.div`
  opacity: 0;
  scale: 0.6;
  overflow: hidden;
  &.visible {
    animation: ${scaleIn} 0.5s 0.3s forwards;
  }
  @media ${laptopAndAbove} {
    width: 50%;
  }
`;

const StyledScoreTimeline = styled(ScoreTimeline)`
  padding-left: 50%;
  overflow: visible;
`;

const Title = styled(BrandL)`
  @media ${laptopAndAbove} {
    font: var(--t-brand-xxl);
  }
`;

const plusIcon = (
  <FontAwesomeIcon icon={iconPlusCircle} color="var(--c-score-high)" />
);

const minusIcon = <FontAwesomeIcon icon={iconMinus} color="var(--c-red-400)" />;

const timeline = [
  {
    minute: 18,
    points: 25,
    icon: plusIcon,
  },
  {
    minute: 25,
    points: -3,
    icon: minusIcon,
  },
  {
    minute: 37,
    points: -3,
    icon: minusIcon,
  },
  {
    minute: 46,
    points: 6,
    icon: plusIcon,
  },
  {
    minute: 59,
    points: -10,
    icon: minusIcon,
  },
  {
    minute: 65,
    points: 9,
    icon: plusIcon,
  },
  {
    minute: 85,
    points: -3,
    icon: minusIcon,
  },
  {
    minute: 90,
    points: 30,
    icon: plusIcon,
  },
];

export const ScorePoints = ({ card }: { card: ScorePoints_card }) => {
  const isCurrent = true;
  const [loopCount, setLoopCount] = useState<number | undefined>(undefined);
  const [currentActionIndex, setCurrentActionIndex] = useState<
    number | undefined
  >(0);

  useInterval(
    () => {
      const nextIndex = (currentActionIndex ?? 0) + 1;
      if (nextIndex === timeline.length) {
        setLoopCount(v => v ?? 0 + 1);
      }
      setCurrentActionIndex(v => {
        if (typeof v === 'undefined') {
          return 0;
        }
        if (v + 1 === timeline.length) {
          return undefined;
        }
        return v + 1;
      });
    },
    isCurrent ? 1000 : null
  );

  const lastAction =
    typeof currentActionIndex === 'undefined'
      ? null
      : timeline.at(currentActionIndex)!;

  const score =
    15 +
    timeline.reduce((acc, { points }, index) => {
      if (
        typeof currentActionIndex === 'undefined' ||
        index > currentActionIndex
      ) {
        return acc;
      }
      return acc + points;
    }, 0);

  return (
    <Root id="score">
      <TextContainer>
        <BrandM uppercase color="var(--c-limited)">
          <FormattedMessage defaultMessage="How to score points" />
        </BrandM>
        <Title>
          <FormattedMessage defaultMessage="Your Players. Real Stats." />
        </Title>
        <BodyL className="text-center">
          <FormattedMessage
            defaultMessage="Big moments like goals and assists make huge contributions,
but passes, tackles, and 40+ other stats can add up to make the real difference"
          />
        </BodyL>
      </TextContainer>
      <CardContainer>
        <CardImg src={card.pictureUrl} alt={card.slug} width={160} />
        <Score
          key={loopCount}
          score={score}
          animationDelayMs={0}
          scoreStatus={ScoreStatus.FINAL}
        />
      </CardContainer>
      <TimelineWrapper className={classNames({ visible: isCurrent })}>
        <StyledScoreTimeline
          timeline={timeline}
          selectedMinute={lastAction?.minute || 0}
        />
      </TimelineWrapper>
    </Root>
  );
};

ScorePoints.fragments = {
  card: gql`
    fragment ScorePoints_card on Card {
      slug
      pictureUrl
    }
  ` as TypedDocumentNode<ScorePoints_card>,
};
